import { Component, Prop, Element, Method, Event, EventEmitter, h, Host } from '@stencil/core';

/**
 * Alerts notify the user of important information in a timely and effective manner.
 */

@Component({
  tag: 'som-alert',
  styleUrl: 'som-alert.scss'
})
export class SomAlert {
  @Element() el: HTMLElement;

  /**
   * This property styles the component which includes color and default icon.
   */
  @Prop() type: 'success' | 'warning' | 'error' | 'info' = 'success';

  /**
   * This property sets the headline of the alert.
   */
  @Prop() headline: string;

  /**
   * This boolean sets whether to include an icon or not.
   */
  @Prop() noIcon: boolean = false;

  /**
   * This property determines if an alert can be dismissed.
   */
  @Prop() dismissible: boolean = false;

  /**
   * This boolean sets the initial state of alert.
   */
  @Prop({ reflect: true }) hidden: boolean = false;

  /**
   * This event is emitted when the alert is dismissed.
   */
  @Event() somDismissed!: EventEmitter<void>;

  /**
   * This public method is used to show an alert.
   * @returns Promise will resolve after alert is shown.
   */
  @Method()
  async show(): Promise<void> {
    return new Promise(resolve => {
      this.hidden = false;
      resolve();
    });
  }

  /**
   * This public method is used to hide an alert.
   * @returns Promise will resolve after alert is hidden.
   */
  @Method()
  async hide(): Promise<void> {
    return new Promise(resolve => {
      this.hidden = true;
      this.somDismissed.emit();
      resolve();
    });
  }

  _getDismissProps() {
    let props = {
      'name': 'icon-cancel',
      'aria-label': 'dismiss',
      'color': 'primary',
      'size': 'md',
    };

    switch (this.type.toLowerCase()) {
      case 'success':
        props.color = 'success';
        break;
      case 'warning':
        props.color = 'warning';
        break;
      case 'error':
        props.color = 'danger';
        break;
      case 'info':
        props.color = 'blue';
      default:
        break;
    }

    return props;
  }

  /**
   * Set the appropriate CSS class
   */
  _getStyles() {
    let alertStyle = ['alert'];

    switch (this.type.toLowerCase()) {
      case 'success':
        alertStyle.push('alert-success');
        break;
      case 'warning':
        alertStyle.push('alert-warning');
        break;
      case 'error':
        alertStyle.push('alert-danger');
        break;
      case 'info':
        alertStyle.push('alert-info');
        break;
      default:
        alertStyle.push('alert-success');
        break;
    }

    return alertStyle.join(' ');
  }

  /**
   * Get properties for <som-icon />
   */
  _getIcon() {
    let icon;

    switch (this.type.toLowerCase()) {
      case 'success':
        icon = `<som-icon name="checkmark-outline" display="block"></som-icon>`;
        break;
      case 'warning':
        icon = `<som-icon name="warning-alt" display="block"></som-icon>`;
        break;
      case 'error':
        icon = `<som-icon name="error" display="block"></som-icon>`;
        break;
      case 'info':
        icon = `<som-icon name="information" display="block"></som-icon>`;
        break;
      default:
        icon = `<som-icon name="information" display="block"></som-icon>`;
        break;
    }

    return icon;
  }


  render() {
    return (
      <Host class={`alert-container ${this._getStyles()}-container ${(this.hidden ? 'alert-dismissed' : '')}`}>
        <div class={this._getStyles()} role="alert">
          <div class="alert-inner">
            {!this.noIcon ? <span class={"icon-container" + (this.headline ? '' : ' no-headline')} aria-hidden="true" innerHTML={this._getIcon()}></span> : null}
            <div class="alert-content">
              {this.headline ? <div class={"headline" + (!this.noIcon ? ' has-icon' : '')}>{this.headline}</div> : null}

              {this.el.innerHTML.length > 0 ? (
                <div class={!this.noIcon ? 'has-icon' : ''}>
                  <slot />
                </div>
              ) : null}
            </div>
            {this.dismissible ? (
              <button class="dismiss" onClick={() => this.hide()} aria-label="Dismiss">
                <span aria-hidden="true">
                  <som-icon name="close" display="block"></som-icon>
                </span>
              </button>
            ) : null}
          </div>
        </div>
      </Host>
    )
  }
}
